import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import {
  DEFAULT_DISPLAY_TIMEZONE,
  getDisplayTimezone,
  persistDisplayTimezone,
} from '@/lib/datetime';

interface TimezoneContextType {
  timezone: string;
  setTimezone: (timeZone: string) => void;
}

const TimezoneContext = createContext<TimezoneContextType | null>(null);

function TimezoneSubtree({ children }: { children: ReactNode }) {
  const { timezone } = useTimezone();
  return (
    <div className="contents" data-display-timezone={timezone}>
      {children}
    </div>
  );
}

export function TimezoneProvider({ children }: { children: ReactNode }) {
  const [timezone, setTimezoneState] = useState(getDisplayTimezone);

  const setTimezone = useCallback((timeZone: string) => {
    persistDisplayTimezone(timeZone);
    setTimezoneState(timeZone);
  }, []);

  const value = useMemo(() => ({ timezone, setTimezone }), [timezone, setTimezone]);

  return (
    <TimezoneContext.Provider value={value}>
      <TimezoneSubtree>{children}</TimezoneSubtree>
    </TimezoneContext.Provider>
  );
}

export function useTimezone() {
  const context = useContext(TimezoneContext);
  if (!context) {
    return {
      timezone: DEFAULT_DISPLAY_TIMEZONE,
      setTimezone: () => {},
    };
  }
  return context;
}
